import {
  type AudioUploadInput,
  audioUploadCreatedSchema,
  audioUploadCreateSchema,
  audioUploadSchema,
  publicIdSchema,
  type RecordingCreateInput,
  recordingCreateSchema,
  type VisitAudioReplaceBody,
  type VisitCreateInput,
  visitAudioReplaceSchema,
  visitAudioSchema,
  visitCreateSchema,
  visitResultSchema,
} from "@rosc/contracts";
import { referenceClient } from "@/lib/orpc/client";

type Identity = { userUid: string; signal?: AbortSignal };
export type AudioVisitInput = Extract<
  VisitCreateInput,
  { audioUploadId: string }
>;
type Grant = Extract<
  Awaited<ReturnType<typeof createAudioUpload>>,
  { status: "pending" }
>["upload"];
export const createAudioUpload = async (
  input: AudioUploadInput,
  identity: Identity & { key: string },
) => {
  const body = audioUploadCreateSchema.parse(input);
  const result = audioUploadCreatedSchema.parse(
    await referenceClient.createAudioUpload(
      { body, headers: { "idempotency-key": identity.key } },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
  if (
    result.status === "pending" &&
    (result.upload.headers["content-type"] !== body.contentType ||
      result.upload.headers["x-amz-checksum-sha256"] !== body.checksumSha256)
  )
    throw Error("Upload grant does not match this file");
  return result;
};
export const completeAudioUpload = async (
  id: string,
  input: AudioUploadInput,
  identity: Identity,
) => {
  const result = audioUploadSchema.parse(
    await referenceClient.completeAudioUpload(
      { params: { id }, body: {} },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
  if (
    result.id !== id ||
    result.status !== "uploaded" ||
    result.completedAt === null ||
    result.contentType !== input.contentType ||
    result.byteLength !== input.byteLength
  )
    throw Error("Completed upload does not match this file");
  return result;
};
export const createAudioVisit = async (
  input: AudioVisitInput,
  identity: Identity & { key: string },
) => {
  const body = visitCreateSchema.parse(input);
  if (!("audioUploadId" in body)) throw Error("Audio upload required");
  return visitResultSchema.parse(
    await referenceClient.createVisit(
      { body, headers: { "idempotency-key": identity.key } },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
};
export const putAudioUpload = async (
  grant: Grant,
  file: Blob,
  {
    signal,
    onProgress,
  }: {
    signal?: AbortSignal;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  },
): Promise<void> => {
  signal?.throwIfAborted();
  if (file.type !== grant.headers["content-type"])
    throw Error("Upload grant does not match this file");
  const url = new URL(grant.url);
  if (url.protocol !== "https:" || url.username || url.password || url.hash)
    throw Error("Invalid upload destination");
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const cleanup = (): void => {
      signal?.removeEventListener("abort", abort);
      xhr.onload = xhr.onerror = xhr.ontimeout = xhr.onabort = null;
      xhr.upload.onprogress = null;
    };
    const abort = (): void => {
      cleanup();
      xhr.abort();
      reject(new DOMException("Upload cancelled", "AbortError"));
    };
    const fail = (): void => {
      cleanup();
      reject(Error("Audio upload was not confirmed. Retry the same file."));
    };
    xhr.open("PUT", url.href);
    xhr.withCredentials = false;
    for (const [key, value] of Object.entries(grant.headers))
      xhr.setRequestHeader(key, value);
    xhr.onload = (): void => {
      if (xhr.status !== 200 && xhr.status !== 412) {
        fail();
        return;
      }
      cleanup();
      resolve();
    };
    xhr.onerror = xhr.ontimeout = fail;
    xhr.onabort = abort;
    xhr.upload.onprogress = (event): void => {
      if (event.lengthComputable)
        onProgress?.({ loaded: event.loaded, total: event.total });
    };
    signal?.addEventListener("abort", abort, { once: true });
    try {
      signal?.throwIfAborted();
      xhr.send(file);
    } catch {
      if (signal?.aborted) abort();
      else fail();
    }
  });
};

const visitAudioId = (id: string): string => publicIdSchema.parse(id);
export const getVisitAudio = async (id: string, identity: Identity) =>
  visitAudioSchema.parse(
    await referenceClient.getVisitAudio(
      { params: { public_id: visitAudioId(id) } },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
export const replaceVisitAudio = async (
  id: string,
  input: VisitAudioReplaceBody,
  identity: Identity & { key: string },
) => {
  const body = visitAudioReplaceSchema.parse(input);
  const result = visitResultSchema.parse(
    await referenceClient.replaceVisitAudio(
      {
        params: { public_id: visitAudioId(id) },
        body,
        headers: { "idempotency-key": identity.key },
      },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
  if (result.public_id !== id)
    throw Error("Audio attachment returned another visit");
  return result;
};

export const createAudioRecording = async (
  parent: string,
  // Audio only, or a live transcript with its recorded audio.
  input: RecordingCreateInput & { audioUploadId: string },
  identity: Identity & { key: string },
) => {
  const body = recordingCreateSchema.parse(input);
  if (!body.audioUploadId) throw Error("Audio upload required");
  return visitResultSchema.parse(
    await referenceClient.createRecording(
      {
        params: { public_id: visitAudioId(parent) },
        body,
        headers: { "idempotency-key": identity.key },
      },
      {
        signal: identity.signal,
        context: { expectedUserUid: identity.userUid },
      },
    ),
  );
};
